import React, { useState } from 'react';
import {
    Box, Typography, Grid, TextField, Button,
    Divider, Stack, IconButton, MenuItem, Autocomplete, Chip
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

const COMPETENCY_OPTIONS = [
    "Communication Skills", "Teamwork & Collaboration", "Problem Solving",
    "Quality of Work", "Punctuality & Reliability", "Leadership",
    "Technical Proficiency", "Adaptability"
];

// Mock Employee Data - Replace with your API data
const EMPLOYEE_LIST = [
    { id: 1, name: 'Alex Johnson', email: 'alex@company.com' },
    { id: 2, name: 'Sarah Williams', email: 'sarah@company.com' },
    { id: 3, name: 'Michael Chen', email: 'michael@company.com' },
    { id: 4, name: 'Emma Davis', email: 'emma@company.com' },
];

function CreateAppraisal({ onClose }) {
    const [formData, setFormData] = useState({
        assignedEmployees: [], // Stores selected employee objects
        appraisalPeriod: '',
        selectedCompetencies: [{ name: '' }],
        strengths: '',
        summary: '',
    });
    const [error, setError] = useState('');

    const addCompetency = () => {
        setFormData({
            ...formData,
            selectedCompetencies: [...formData.selectedCompetencies, { name: '' }]
        });
    };

    const removeCompetency = (index) => {
        const list = [...formData.selectedCompetencies];
        list.splice(index, 1);
        setFormData({ ...formData, selectedCompetencies: list });
    };

    const handleCompetencyUpdate = (index, value) => {
        const list = [...formData.selectedCompetencies];
        list[index].name = value;
        setFormData({ ...formData, selectedCompetencies: list });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Validation: Ensure at least one employee is selected
        if (formData.assignedEmployees.length === 0) {
            setError("Please assign at least one employee.");
            return;
        }
        setError('');
        onClose();
    };

    return (
        <Box sx={{ p: 2 }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* --- Assignment Section --- */}
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle1" fontWeight="600" mb={1}>
                            Assignment
                        </Typography>
                        <Autocomplete
                            multiple
                            options={EMPLOYEE_LIST}
                            getOptionLabel={(option) => option.name}
                            value={formData.assignedEmployees}
                            onChange={(event, newValue) => {
                                setFormData({ ...formData, assignedEmployees: newValue });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Assign to Employees"
                                    placeholder="Search employees..."
                                    required={formData.assignedEmployees.length === 0}
                                    size="small"
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option.name}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        {...getTagProps({ index })}
                                    />
                                ))
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            size='small'
                            label="Current Appraisal Period (e.g., Q1 2026)"
                            required
                            value={formData.appraisalPeriod}
                            onChange={(e) => setFormData({ ...formData, appraisalPeriod: e.target.value })}
                        />
                    </Grid>
                </Grid>

                <Stack direction="row" justifyContent="space-between" alignItems="center" my={3}>
                    <Typography variant="subtitle1" fontWeight="600">Core Competencies</Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addCompetency}
                        disabled={formData.selectedCompetencies.length >= COMPETENCY_OPTIONS.length}
                        sx={{ textTransform: 'none' }}
                    >
                        Add Competency
                    </Button>
                </Stack>

                <Grid container spacing={2}>
                    {formData.selectedCompetencies.map((item, index) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Select Competency"
                                    size="small"
                                    required
                                    value={item.name}
                                    onChange={(e) => handleCompetencyUpdate(index, e.target.value)}
                                >
                                    {COMPETENCY_OPTIONS.map((option) => {
                                        const isSelectedElsewhere = formData.selectedCompetencies.some(
                                            (comp, i) => comp.name === option && i !== index
                                        );
                                        return (
                                            <MenuItem key={option} value={option} disabled={isSelectedElsewhere}>
                                                {option}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>

                                {formData.selectedCompetencies.length > 1 && (
                                    <IconButton color="error" onClick={() => removeCompetency(index)}>
                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Expectations / Key Strengths to focus on"
                            required
                            value={formData.strengths}
                            onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Summary"
                            value={formData.summary}
                            onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                        />
                    </Grid>

                    {error && (
                        <Grid size={{ xs: 12 }}>
                            <Typography color="error" variant="body2">
                                {error}
                            </Typography>
                        </Grid>
                    )}

                    <Grid size={{ xs: 12 }}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                            <Button variant="outlined" color="inherit" onClick={onClose} sx={{ textTransform: 'none' }}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{ px: 4, textTransform: 'none' }}
                            >
                                Create Appraisal
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
}

export default CreateAppraisal;